import {
  type RemoteIntroPromoCohort,
  type RemoteIntroPromoEntrySurface,
} from '@heidi/growth/contracts';
import { atom } from 'jotai';

// ── Modal state ───────────────────────────────────────────────────────────────
// Dismiss / engage persistence lives on the backend via `ModalId.REMOTE_INTRO_PROMO`
// in `useRemoteIntroPromoSeen` — one ID covers all three surfaces because a
// user only ever sees one cohort variant of the promo at a time.

/**
 * Open state for the shared promo modal. The banner CTA and the menu card both
 * write to this atom so they open the same Dialog instance.
 */
export const remoteIntroPromoModalOpenAtom = atom<boolean>(false);

/**
 * Surface that opened the promo modal. Read by the modal CTA so storefront
 * UTMs and the modal-click event can be attributed back to the originating
 * surface (billing banner vs profile menu vs direct). `null` means the modal
 * was opened without an explicit entry surface (e.g. dev tooling).
 */
/**
 * Re-exported name-intact, not declared. The union moved to
 * `@heidi/growth/contracts` with the payload types that use it — a duplicated
 * union is how one event becomes two — and it is still named at this path by the
 * modal shell, its content component and their suites.
 */
export type { RemoteIntroPromoEntrySurface };

export const remoteIntroPromoModalEntrySurfaceAtom =
  atom<RemoteIntroPromoEntrySurface | null>(null);

/**
 * Dev/staging-only escape hatch for QA — when set, eligibility short-circuits
 * to the chosen cohort and the banner ignores its dismissed-state gate.
 * Driven only through `window.__remoteIntroPromo.forceShow(cohort)` which is
 * registered in non-production builds only. `null` means no force in effect.
 */
export const remoteIntroPromoDevForceCohortAtom = atom<RemoteIntroPromoCohort | null>(
  null
);
